'use client';

import { useState, useCallback } from 'react';
import { CartItem } from '@/lib/cartStore';

export function useCart(initialItems: CartItem[], refCode?: string) {
  const [items, setItems] = useState<CartItem[]>(initialItems);

  const addItem = useCallback(async (productId: string) => {
    const res = await fetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, action: 'add', qty: 1, ref: refCode }),
    });
    const data = await res.json();
    if (data.ok) setItems(data.items);
    return data as { ok: boolean; error?: string; requireLogin?: boolean };
  }, [refCode]);

  const removeItem = useCallback(async (productId: string) => {
    const res = await fetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, action: 'remove', ref: refCode }),
    });
    const data = await res.json();
    if (data.ok) setItems(data.items);
  }, [refCode]);

  const setQtyItem = useCallback(async (productId: string, qty: number) => {
    const res = await fetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, action: 'set', qty, ref: refCode }),
    });
    const data = await res.json();
    if (data.ok) setItems(data.items);
  }, [refCode]);

  const total = items.reduce((s, i) => s + i.price * i.qty, 0);

  return { items, total, addItem, removeItem, setQtyItem };
}

interface ProductOption {
  id: string;
  option_label: string;
  price: number;
  list_price: number | null;
  stock: number;
}

interface Product {
  id: string;
  name: string;
  price: number;
  list_price: number | null;
  image_url: string | null;
  stock: number;
  has_options?: boolean;
  adult_only?: number | boolean;
  options?: ProductOption[];
}

interface ShopProps {
  products: Product[];
  initialCart: CartItem[];
  refCode?: string;
  /**
   * Server-derived STOCK_ENFORCEMENT flag. When true, stock<=0 products show a
   * "품절" badge and disable 담기. When false (go-live default), stock is advisory
   * only — a 0-stock (un-backfilled) product never blocks a real purchase.
   */
  stockEnforced?: boolean;
}

function ProductCard({
  product, items, loading, stockEnforced, onAdd, onMinus,
}: {
  product: Product;
  items: CartItem[];
  loading: string | null;
  stockEnforced: boolean;
  onAdd: (id: string) => void;
  onMinus: (id: string, currentQty: number) => void;
}) {
  const opts = product.options ?? [];
  const firstAvailable = opts.find(o => !(stockEnforced && o.stock <= 0)) ?? opts[0];
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(firstAvailable?.id);
  const selectedOption = opts.find(o => o.id === selectedOptionId);

  const sellId = product.has_options ? selectedOption?.id : product.id;
  const price = product.has_options ? (selectedOption?.price ?? 0) : product.price;
  const listPrice = product.has_options ? (selectedOption?.list_price ?? null) : product.list_price;
  const stock = product.has_options ? (selectedOption?.stock ?? 0) : product.stock;

  const inCart = sellId ? items.find(i => i.id === sellId) : undefined;
  const qty = inCart?.qty ?? 0;
  const soldOut = stockEnforced && stock <= 0;
  const noOptionAvailable = product.has_options && !sellId;
  const badge = noOptionAvailable ? '품절' : soldOut ? '품절' : stock > 0 ? `${stock}개 남음` : null;

  return (
    <div className="border border-gray-200 rounded-lg p-3 flex items-start gap-3">
      {/* 상품 이미지 + 재고 배지 */}
      <div className="relative w-[90px] h-[90px] shrink-0 rounded-lg bg-gray-100 overflow-hidden">
        {product.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        ) : null}
        {badge && (
          <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-sm font-medium text-center py-1.5">
            {badge}
          </span>
        )}
      </div>

      {/* 상품명(전체 폭) + 옵션선택 + 가격/스테퍼(하단 한 줄) */}
      <div className="min-w-0 flex-1 self-stretch flex flex-col justify-between">
        <div className="font-normal text-sm text-black leading-snug mt-3">
          {!!product.adult_only && (
            <span className="inline-block align-middle mr-1 text-[10px] font-bold bg-red-600 text-white rounded px-1">19</span>
          )}
          {product.name}
        </div>

        {product.has_options && (
          <select
            value={selectedOptionId ?? ''}
            onChange={e => setSelectedOptionId(e.target.value || undefined)}
            className="mt-1 w-full border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-900 bg-white focus:outline-none focus:border-blue-400"
          >
            {opts.length === 0 && <option value="">옵션 준비중</option>}
            {opts.map(o => (
              <option key={o.id} value={o.id} disabled={stockEnforced && o.stock <= 0}>
                {o.option_label} — {o.price.toLocaleString()}원{stockEnforced && o.stock <= 0 ? ' (품절)' : ''}
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="flex items-center gap-1.5">
            {listPrice != null && listPrice > price && (
              <span className="text-xs text-red-500 line-through decoration-red-500">{listPrice.toLocaleString()}원</span>
            )}
            <div className="font-normal text-sm text-black">{price.toLocaleString()}원</div>
          </div>

          {/* 수량 스테퍼 (0부터 시작, 담기 버튼 없이 +로 바로 담김) */}
          <div className="shrink-0 inline-flex items-center rounded-full border border-gray-200 bg-white">
            <button
              onClick={() => sellId && onMinus(sellId, qty)}
              disabled={!sellId || loading === sellId || qty === 0}
              aria-label="수량 줄이기"
              className="w-9 h-9 flex items-center justify-center text-gray-700 font-bold rounded-l-full hover:bg-gray-50 disabled:opacity-40"
            >
              −
            </button>
            <span className="w-8 text-center text-sm font-bold text-black tabular-nums">
              {sellId && loading === sellId ? '·' : qty}
            </span>
            <button
              onClick={() => sellId && onAdd(sellId)}
              disabled={!sellId || loading === sellId || soldOut}
              aria-label="수량 늘리기"
              className="w-9 h-9 flex items-center justify-center text-blue-600 font-bold rounded-r-full hover:bg-gray-50 disabled:opacity-50"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShopClient({ products, initialCart, refCode, stockEnforced = false }: ShopProps) {
  const { items, total, addItem, setQtyItem } = useCart(initialCart, refCode);
  const [loading, setLoading] = useState<string | null>(null);

  const cartCount = items.reduce((s, i) => s + i.qty, 0);

  const handleAdd = async (productId: string) => {
    setLoading(productId);
    const data = await addItem(productId);
    setLoading(null);
    if (!data.ok) {
      if (data.requireLogin) {
        alert('로그인이 필요한 상품입니다.');
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      } else if (data.error) {
        alert(data.error);
      }
    }
  };

  const handleMinus = async (productId: string, currentQty: number) => {
    setLoading(productId);
    await setQtyItem(productId, currentQty - 1);
    setLoading(null);
  };

  return (
    <div>
      {/* 상품 목록 */}
      <div className="space-y-3 mt-2">
        {products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            items={items}
            loading={loading}
            stockEnforced={stockEnforced}
            onAdd={handleAdd}
            onMinus={handleMinus}
          />
        ))}
      </div>

      {/* 미니 장바구니 요약 */}
      {items.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex justify-between items-center">
            <span className="text-sm text-blue-700 font-medium">
              장바구니 {cartCount}개
            </span>
            <span className="font-bold text-blue-800">{total.toLocaleString()}원</span>
          </div>
          <a
            href={!refCode ? '/cart' : `/cart/${refCode}`}
            className="mt-2 block w-full text-center bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
          >
            결제하기 →
          </a>
        </div>
      )}
    </div>
  );
}
